import { View, Text, StyleSheet } from "react-native";

import type { FormDefinition } from "@/lib/api";
import { colors, fonts, spacing, radius, shadows } from "@/lib/theme";

interface MedicalInfoCardProps {
    diagnoses: Array<{ name: string; icdCode?: string }>;
    allergies: FormDefinition["patient"] extends infer P
        ? P extends { allergies: infer A }
            ? A
            : null
        : null;
    medications: Array<{ name: string; dosage?: string; strength?: string; orderedDose?: string }>;
}

function renderAllergies(
    allergies: { medication?: string[]; food?: string[]; environmental?: string[] } | null
) {
    if (!allergies || typeof allergies !== "object" || Array.isArray(allergies)) {
        return <Text style={styles.emptyText}>No Known Allergies (NKA)</Text>;
    }

    const sections = [
        { label: "Medication", items: allergies.medication ?? [] },
        { label: "Food", items: allergies.food ?? [] },
        { label: "Environmental", items: allergies.environmental ?? [] }
    ].filter((s) => s.items.length > 0);

    if (sections.length === 0) {
        return <Text style={styles.emptyText}>No Known Allergies (NKA)</Text>;
    }

    return (
        <>
            {sections.map((s) => (
                <View key={s.label} style={styles.allergySection}>
                    <Text style={styles.allergyLabel}>{s.label}: </Text>
                    <Text style={styles.allergyValue}>{s.items.join(", ")}</Text>
                </View>
            ))}
        </>
    );
}

export function MedicalInfoCard({ diagnoses, allergies, medications }: MedicalInfoCardProps) {
    return (
        <View style={styles.card}>
            <Text style={styles.sectionLabel}>MEDICAL INFORMATION</Text>

            {/* Diagnoses */}
            <Text style={styles.categoryTitle}>Diagnoses</Text>
            {diagnoses.length > 0 ? (
                diagnoses.map((d, i) => (
                    <Text key={i} style={styles.itemText}>
                        {d.name}
                        {d.icdCode ? ` (${d.icdCode})` : ""}
                    </Text>
                ))
            ) : (
                <Text style={styles.emptyText}>Diagnoses loading...</Text>
            )}

            {/* Allergies */}
            <Text style={[styles.categoryTitle, styles.categoryMargin]}>Allergies</Text>
            {renderAllergies(allergies as any)}

            {/* Medications */}
            <Text style={[styles.categoryTitle, styles.categoryMargin]}>Medications</Text>
            {medications.length > 0 ? (
                medications.map((m, i) => (
                    <Text key={i} style={styles.itemText}>
                        {m.name}
                        {(m.strength ?? m.dosage) ? ` ${m.strength ?? m.dosage}` : ""}
                        {m.orderedDose ? ` — ${m.orderedDose}` : ""}
                    </Text>
                ))
            ) : (
                <Text style={styles.emptyText}>Medications loading...</Text>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    card: {
        marginHorizontal: spacing.base,
        marginTop: spacing.md,
        backgroundColor: colors.surface,
        borderRadius: radius.lg,
        borderWidth: 1.5,
        borderColor: colors.border,
        padding: spacing.base,
        ...shadows.sm
    },
    sectionLabel: {
        fontFamily: fonts.bold,
        fontSize: 10,
        color: colors.teal500,
        letterSpacing: 1,
        textTransform: "uppercase",
        marginBottom: spacing.md
    },
    categoryTitle: {
        fontFamily: fonts.semiBold,
        fontSize: 13,
        color: colors.textPrimary,
        marginBottom: spacing.xs
    },
    categoryMargin: {
        marginTop: spacing.md
    },
    itemText: {
        fontFamily: fonts.regular,
        fontSize: 13,
        color: colors.textSecondary,
        lineHeight: 20,
        paddingLeft: spacing.md
    },
    allergySection: {
        flexDirection: "row",
        flexWrap: "wrap",
        paddingLeft: spacing.md
    },
    allergyLabel: {
        fontFamily: fonts.semiBold,
        fontSize: 13,
        color: colors.textSecondary
    },
    allergyValue: {
        fontFamily: fonts.regular,
        fontSize: 13,
        color: colors.textSecondary
    },
    emptyText: {
        fontFamily: fonts.regular,
        fontSize: 13,
        color: colors.textTertiary,
        fontStyle: "italic",
        paddingLeft: spacing.md
    }
});
